import { makeAutoObservable } from 'mobx';
import type {
  IContractPreview,
  IContractPreviewItem,
  IPolicyPreview,
  IPolicyPreviewItem,
  PolicyResearchTableColumnId,
  PolicyResearchTableColumnSetting,
} from '../types/chat';
import { POLICY_RESEARCH_TABLE_COLUMN_OPTIONS } from '../types/chat';
import type { ColumnFilterState, FilterOperator } from '../types/table';
import type MainStore from './MainStore';
import { apiService } from '../services';
import { isNationalValue } from '../components/helpers/preChatsHelper';
import {
  CONTRACTS_FILTER_FIELDS,
  CONTRACTS_FILTERS_STORAGE_KEY,
  loadPersistedResearchFilters,
  POLICIES_FILTER_FIELDS,
  POLICIES_FILTERS_STORAGE_KEY,
  savePersistedResearchFilters,
} from '../components/helpers/researchFiltersStorage';

const CONTRACTS_PAGE_SIZE = 20;

export interface ChatCategoryFilters {
  payer: string;
  state: string;
  lineOfBusiness: string;
  dateOfService: string;
  contractSearchQuery: string;
  policySearchQuery: string;
}

const DEFAULT_FILTERS: ChatCategoryFilters = {
  payer: '',
  state: '',
  lineOfBusiness: '',
  dateOfService: '',
  contractSearchQuery: '',
  policySearchQuery: ''
};

export interface ChatCategoryContractsSort {
  by: string;
  direction: 'asc' | 'desc';
}

const DEFAULT_CONTRACTS_SORT: ChatCategoryContractsSort = { by: '', direction: 'asc' };

// Side panel resize constants
export const SIDE_PANEL_DEFAULT_WIDTH = 35;
export const SIDE_PANEL_MIN_WIDTH = 25;
/** Fallback max width when not in browser; runtime max = windowWidth - sidebarWidth - SIDE_PANEL_VIEWPORT_MARGIN */
export const SIDE_PANEL_MAX_WIDTH = 80;

export default class ChatCategoryStore {
  // Reference to MainStore (set by MainStore after creation) for this.mainStore.chatStore / this.mainStore.chatCategoryStore
  mainStore: MainStore | null = null;

  isFirstLoad: boolean = true;
  isLoadingContracts: boolean = false;
  isSearching: boolean = false;
  filters: ChatCategoryFilters = { ...DEFAULT_FILTERS };

  // Side panel resize state
  sidePanelWidth: number = SIDE_PANEL_DEFAULT_WIDTH;
  isSidePanelResizing: boolean = false;

  // Contract preview list (contracts table data)
  contractsData: IContractPreview = { total: 0, contracts: [] };
  isLoadingMore: boolean = false;
  hasMoreData: boolean = true;
  // Column/direction currently sorted on the contract research table (backend-sorted); by: '' = default (title asc)
  contractsSort: ChatCategoryContractsSort = { ...DEFAULT_CONTRACTS_SORT };
  // Excel-style header filters on the contracts table (columnId -> { operator, values }), applied backend-side
  columnFilters: Record<string, ColumnFilterState> = {};
  triggerClearDrawerData: boolean = true;
  // True while the contract research "Ask Reve" welcome/chat flow replaces the contracts table
  isAskingReveForContract: boolean = false;
  // True while the policy research "Ask Reve" welcome/chat flow replaces the policy research table
  isAskingReveForPolicy: boolean = false;

  isPoliciesDocumentsDrawerOpen: boolean = false;

  // Browsable policy research table (PolicyQuestionsPage) -- also backs the in-chat policy
  // documents drawer/rail (PolicyPreview), narrowed there to the selected payer/state.
  policyResearchData: IPolicyPreview = { total: 0, policies: [] };
  isLoadingPolicyResearch: boolean = false;
  policyResearchIsLoadingMore: boolean = false;
  policyResearchHasMoreData: boolean = true;
  policyResearchSort: ChatCategoryContractsSort = { ...DEFAULT_CONTRACTS_SORT };
  policyResearchColumnFilters: Record<string, ColumnFilterState> = {};
  // Policy research table column settings (visibility + pin/required), keyed by column id.
  // Saved with the rest of the policy research filters ("filters:policies").
  policyResearchColumnSettings: PolicyResearchTableColumnSetting[] = ChatCategoryStore.mergePolicyResearchColumnSettings(null);

  // Sequencing for fetchContracts: incremented on every call so a stale request's
  // response (search/sort/filter/refresh/loadMore can all fire in quick succession)
  // can be told apart from the latest one and dropped instead of overwriting it.
  contractsRequestId: number = 0;
  contractsAbortController: AbortController | null = null;

  // Same sequencing for fetchPolicyResearch -- see contractsRequestId/contractsAbortController.
  policyResearchRequestId: number = 0;
  policyResearchAbortController: AbortController | null = null;

  constructor(mainStore: MainStore) {
    makeAutoObservable(this, {
      mainStore: false, // reference only, don't make observable
      contractsRequestId: false,
      contractsAbortController: false,
      policyResearchRequestId: false,
      policyResearchAbortController: false,
    });
    this.mainStore = mainStore;
  }

  setSidePanelWidth = (newPercent: number) => {
    if(newPercent > SIDE_PANEL_MAX_WIDTH || newPercent < SIDE_PANEL_MIN_WIDTH){
      return
    }

    this.sidePanelWidth = newPercent;
    localStorage.setItem('chatSidePanelWidth', String(this.sidePanelWidth));
  };

  setIsSidePanelResizing = (resizing: boolean) => {
    this.isSidePanelResizing = resizing;
  };

  clearChatCategoryStore = (resetFilters: boolean = false) => {
    if (resetFilters) {
      this.filters = { ...DEFAULT_FILTERS };
      this.isFirstLoad = true;
      this.contractsData = { total: 0, contracts: [] };
      this.isLoadingMore = false;
      this.hasMoreData = true;
      this.contractsSort = { ...DEFAULT_CONTRACTS_SORT };
      this.columnFilters = {};
      this.triggerClearDrawerData = true;
      this.isPoliciesDocumentsDrawerOpen = false;
      this.isAskingReveForContract = false;
      this.isAskingReveForPolicy = false;
      this.policyResearchData = { total: 0, policies: [] };
      this.policyResearchIsLoadingMore = false;
      this.policyResearchHasMoreData = true;
      this.policyResearchSort = { ...DEFAULT_CONTRACTS_SORT };
      this.policyResearchColumnFilters = {};
      this.policyResearchColumnSettings = ChatCategoryStore.mergePolicyResearchColumnSettings(null);
    }
    // Always clear loading state so we're not stuck in a loading state
    this.isLoadingContracts = false;
    this.isLoadingPolicyResearch = false;
    this.isSearching = false;
  };

  // Defaults from POLICY_RESEARCH_TABLE_COLUMN_OPTIONS, overlaid with the saved settings (null = none saved).
  private static mergePolicyResearchColumnSettings(
    saved: PolicyResearchTableColumnSetting[] | null,
  ): PolicyResearchTableColumnSetting[] {
    const defaults: PolicyResearchTableColumnSetting[] = POLICY_RESEARCH_TABLE_COLUMN_OPTIONS.map((col) => ({
      id: col.id as PolicyResearchTableColumnId,
      visible: col.visible !== false,
      required: col.required === true,
    }));
    if (!saved) return defaults;
    const savedById = new Map<string, PolicyResearchTableColumnSetting>(saved.map((setting) => [setting.id, setting]));
    return defaults.map((def) => ({ ...def, ...savedById.get(def.id) }));
  }

  private isPolicyResearchColumnMandatory = (columnId: string): boolean =>
    !!POLICY_RESEARCH_TABLE_COLUMN_OPTIONS.find((col) => col.id === columnId)?.mandatory;

  private getPolicyResearchColumnSetting = (columnId: string): PolicyResearchTableColumnSetting | undefined =>
    this.policyResearchColumnSettings.find((setting) => setting.id === columnId);

  private updatePolicyResearchColumnSetting = (
    columnId: PolicyResearchTableColumnId,
    updates: Partial<Omit<PolicyResearchTableColumnSetting, 'id'>>,
  ) => {
    this.policyResearchColumnSettings = this.policyResearchColumnSettings.map((setting) =>
      setting.id === columnId ? { ...setting, ...updates } : setting,
    );
    this.savePolicyResearchFilters();
  };

  togglePolicyResearchColumnVisibility = (columnId: PolicyResearchTableColumnId) => {
    if (this.isPolicyResearchColumnMandatory(columnId)) return;
    this.updatePolicyResearchColumnSetting(columnId, {
      visible: !this.getPolicyResearchColumnSetting(columnId)?.visible,
    });
  };

  // Columns not listed in POLICY_RESEARCH_TABLE_COLUMN_OPTIONS (e.g. the row actions) are always visible.
  isPolicyResearchColumnVisible = (columnId: string): boolean => {
    if (this.isPolicyResearchColumnMandatory(columnId)) return true;
    return this.getPolicyResearchColumnSetting(columnId)?.visible !== false;
  };

  togglePolicyResearchRequiredColumn = (columnId: PolicyResearchTableColumnId) => {
    if (this.isPolicyResearchColumnMandatory(columnId)) return;
    const nextRequired = !this.isPolicyResearchColumnRequired(columnId);
    this.updatePolicyResearchColumnSetting(
      columnId,
      nextRequired ? { required: true, visible: true } : { required: false },
    );
  };

  isPolicyResearchColumnRequired = (columnId: string): boolean => {
    if (this.isPolicyResearchColumnMandatory(columnId)) return true;
    return !!this.getPolicyResearchColumnSetting(columnId)?.required;
  };

  setTriggerClearDrawerData = (trigger: boolean) => {
    this.triggerClearDrawerData = trigger;
  };

  setIsPoliciesDocumentsDrawerOpen = (open: boolean) => {
    this.isPoliciesDocumentsDrawerOpen = open;
  };

  setIsAskingReveForContract = (isAsking: boolean) => {
    this.isAskingReveForContract = isAsking;
  };

  setIsAskingReveForPolicy = (isAsking: boolean) => {
    this.isAskingReveForPolicy = isAsking;
  };

  setContractsData = (data: IContractPreview) => {
    this.contractsData = data;
  };

  appendContractsData = (contracts: IContractPreviewItem[]) => {
    this.contractsData = {
      ...this.contractsData,
      contracts: [...this.contractsData.contracts, ...contracts],
    };
  };

  setIsLoadingMore = (isLoading: boolean) => {
    this.isLoadingMore = isLoading;
  };

  setHasMoreData = (hasMore: boolean) => {
    this.hasMoreData = hasMore;
  };

  setIsLoadingContracts = (isLoading: boolean) => {
    this.isLoadingContracts = isLoading;
  };

  setIsSearching = (isLoading: boolean) => {
    this.isSearching = isLoading
  }

  get isPoliciesDrawerOpen(): boolean {
    const isFormFilled = !!(this.filters?.payer && this.filters?.state);

    // this.isFirstLoad -> keep drawer opened after first load
    return window?.location?.pathname?.includes('policy-questions') && (isFormFilled || !this.isFirstLoad)
  }

  get isContractsDrawerOpen(): boolean {
    const isFormFilled = !!(this.filters?.payer && this.filters?.state);

    // Keep drawer opened after first load
    return (isFormFilled || !this.isFirstLoad)
  }

  setIsFirstLoad = (isFirstLoad: boolean) => {
    this.isFirstLoad = isFirstLoad;
  };

  setFilters = (filters: Partial<ChatCategoryFilters>) => {
    this.filters = { ...this.filters, ...filters };
  };

  // setFilters + save, for the research pages' own filter controls only. `filters` is
  // shared with the in-chat policy drawer, which keeps calling plain setFilters.
  setContractResearchFilters = (filters: Partial<ChatCategoryFilters>) => {
    this.setFilters(filters);
    this.saveContractResearchFilters();
  };

  setPolicyResearchFilters = (filters: Partial<ChatCategoryFilters>) => {
    this.setFilters(filters);
    this.savePolicyResearchFilters();
  };

  // Sets the column/direction the contracts table is sorted by (backend-driven) and reloads page one.
  setContractsSorting = (sortBy: string, sortDirection: 'asc' | 'desc') => {
    this.contractsSort = { by: sortBy, direction: sortDirection };
    this.saveContractResearchFilters();
    this.fetchContracts();
  };

  // Sets (or clears, when values is empty) a column's header filter and reloads page one --
  // mirrors ClaimsStore.setColumnFilter/clearColumnFilter.
  setColumnFilter = (columnId: string, values: string[], operator: FilterOperator = 'and') => {
    const nonEmptyValues = values.filter((value) => value !== '');
    if (nonEmptyValues.length === 0) {
      this.clearColumnFilter(columnId);
      return;
    }
    this.columnFilters = { ...this.columnFilters, [columnId]: { operator, values: nonEmptyValues } };
    this.saveContractResearchFilters();
    this.fetchContracts();
  };

  clearColumnFilter = (columnId: string) => {
    if (!(columnId in this.columnFilters)) return;
    const next = { ...this.columnFilters };
    delete next[columnId];
    this.columnFilters = next;
    this.saveContractResearchFilters();
    this.fetchContracts();
  };

  // State-only reset (no refetch) -- callers that also change other filters in the same
  // action (e.g. "Clear Filters") should follow this with a single explicit fetchContracts().
  clearAllColumnFilters = () => {
    this.columnFilters = {};
    this.saveContractResearchFilters();
  };

  // Fetches the first batch of contracts for the currently selected payer/state/line of business,
  // search term and column filters. Pass loadMore=true to append the next page instead (called when
  // scrolling to the bottom of the table).
  fetchContracts = async (loadMore: boolean = false) => {
    let lastContract: IContractPreviewItem | undefined;
    if (loadMore) {
      if (this.isLoadingMore || !this.hasMoreData || this.contractsData.contracts.length === 0) {
        return;
      }
      lastContract = this.contractsData.contracts[this.contractsData.contracts.length - 1];
      if (!lastContract?.document_id) return;
    } else {
      this.setHasMoreData(true);
    }

    // Supersede any in-flight contracts request: search, sort, column filters, refresh
    // and loadMore can all fire in quick succession, and without this whichever response
    // lands last would win regardless of request order (e.g. a State change from NY to CA
    // could have NY's response land after CA's). Abort the previous fetch and clear
    // whichever loading flag it owned so it can't get stuck if its response is dropped.
    this.contractsAbortController?.abort();
    this.setIsLoadingMore(false);
    this.setIsLoadingContracts(false);
    const controller = new AbortController();
    this.contractsAbortController = controller;
    const requestId = ++this.contractsRequestId;

    if (loadMore) {
      this.setIsLoadingMore(true);
    } else {
      this.setIsLoadingContracts(true);
    }

    // The pagination cursor must carry the value of whichever column is currently sorted on,
    // since that's the field the backend's composite aggregation paginates over.
    const sortColumn = (this.contractsSort.by || 'title') as keyof IContractPreviewItem;

    try {
      const response = await apiService.getTenantContracts(
        this.filters.payer,
        isNationalValue(this.filters.state) ? undefined : this.filters.state,
        this.filters.lineOfBusiness,
        loadMore ? lastContract?.document_id : undefined,
        loadMore ? lastContract?.[sortColumn] : undefined,
        this.filters.contractSearchQuery,
        this.contractsSort.by || undefined,
        this.contractsSort.direction,
        this.columnFilters,
        controller.signal,
      );

      // A newer fetchContracts call superseded this one -- its response is stale
      // and must not overwrite state the newer request already owns.
      if (requestId !== this.contractsRequestId) return;

      if (response.success && response.data) {
        if (loadMore) {
          if (response.data.contracts.length === 0) {
            this.setHasMoreData(false);
          } else {
            this.appendContractsData(response.data.contracts);
            if (response.data.contracts.length < CONTRACTS_PAGE_SIZE) {
              this.setHasMoreData(false);
            }
          }
        } else {
          this.setContractsData(response.data);
          if (response.data.contracts.length < CONTRACTS_PAGE_SIZE) {
            this.setHasMoreData(false);
          }
        }
      } else {
        console.error('Failed to fetch contracts:', response.error);
        // Only blank the table if we had no data to begin with (e.g. first load);
        // a failed refetch (search/sort/refresh) should leave the existing rows visible.
        if (!loadMore && this.contractsData.contracts.length === 0) {
          this.setContractsData({ total: 0, contracts: [] });
        }
        this.setHasMoreData(false);
      }
    } catch {
      if (requestId !== this.contractsRequestId) return;
      if (!loadMore && this.contractsData.contracts.length === 0) {
        this.setContractsData({ total: 0, contracts: [] });
        this.mainStore?.appStore.setContractToPreview(null);
      }
      this.setHasMoreData(false);
    } finally {
      if (requestId === this.contractsRequestId) {
        if (loadMore) {
          this.setIsLoadingMore(false);
        } else {
          this.setIsLoadingContracts(false);
          this.setIsSearching(false);
        }
      }
    }
  };

  setPolicyResearchData = (data: IPolicyPreview) => {
    this.policyResearchData = data;
  };

  appendPolicyResearchData = (policies: IPolicyPreviewItem[]) => {
    this.policyResearchData = {
      ...this.policyResearchData,
      policies: [...this.policyResearchData.policies, ...policies],
    };
  };

  setIsLoadingPolicyResearch = (isLoading: boolean) => {
    this.isLoadingPolicyResearch = isLoading;
  };

  setPolicyResearchIsLoadingMore = (isLoading: boolean) => {
    this.policyResearchIsLoadingMore = isLoading;
  };

  setPolicyResearchHasMoreData = (hasMore: boolean) => {
    this.policyResearchHasMoreData = hasMore;
  };

  // Sets the column/direction the policy research table is sorted by (backend-driven) and reloads page one.
  setPolicyResearchSorting = (sortBy: string, sortDirection: 'asc' | 'desc') => {
    this.policyResearchSort = { by: sortBy, direction: sortDirection };
    this.savePolicyResearchFilters();
    this.fetchPolicyResearch();
  };

  setPolicyResearchColumnFilter = (columnId: string, values: string[], operator: FilterOperator = 'and') => {
    if (values.every((value) => value === '')) {
      this.clearPolicyResearchColumnFilter(columnId);
      return;
    }
    // Kept as sent, empties included: date ranges are positional [start, end], so dropping an
    // empty start would turn an end-only range ["", end] into a start-only one on the backend.
    this.policyResearchColumnFilters = { ...this.policyResearchColumnFilters, [columnId]: { operator, values } };
    this.savePolicyResearchFilters();
    this.fetchPolicyResearch();
  };

  clearPolicyResearchColumnFilter = (columnId: string) => {
    if (!(columnId in this.policyResearchColumnFilters)) return;
    const next = { ...this.policyResearchColumnFilters };
    delete next[columnId];
    this.policyResearchColumnFilters = next;
    this.savePolicyResearchFilters();
    this.fetchPolicyResearch();
  };

  clearAllPolicyResearchColumnFilters = () => {
    this.policyResearchColumnFilters = {};
    this.savePolicyResearchFilters();
  };

  // Saved research-page filters (localStorage "filters:policies" / "filters:contracts", per tenant and user).
  // Saving happens only from user filter changes: the sort and column filter actions above
  // (each used only by its own research page), set{Contract,Policy}ResearchFilters and the policy
  // research column visibility/pin toggles. A store reset
  // (clearChatCategoryStore, e.g. on Header/Sidebar navigation) never saves, so it can't
  // wipe what the user saved. restore* is state-only (no refetch): each page calls it on
  // mount, after clearChatCategoryStore(true) and before its first fetch.
  restorePolicyResearchFilters = () => {
    const { filters, columnFilters, sort, columnSettings } = loadPersistedResearchFilters(POLICIES_FILTERS_STORAGE_KEY, this.mainStore?.filtersScope ?? null, POLICIES_FILTER_FIELDS);
    this.filters = { ...this.filters, ...filters };
    this.policyResearchColumnFilters = columnFilters;
    this.policyResearchSort = sort;
    this.policyResearchColumnSettings = ChatCategoryStore.mergePolicyResearchColumnSettings(columnSettings ?? null);
  };

  restoreContractResearchFilters = () => {
    const { filters, columnFilters, sort } = loadPersistedResearchFilters(CONTRACTS_FILTERS_STORAGE_KEY, this.mainStore?.filtersScope ?? null, CONTRACTS_FILTER_FIELDS);
    this.filters = { ...this.filters, ...filters };
    this.columnFilters = columnFilters;
    this.contractsSort = sort;
  };

  private savePolicyResearchFilters(): void {
    savePersistedResearchFilters(POLICIES_FILTERS_STORAGE_KEY, this.mainStore?.filtersScope ?? null, {
      filters: Object.fromEntries(POLICIES_FILTER_FIELDS.map((field) => [field, this.filters[field]])),
      columnFilters: this.policyResearchColumnFilters,
      sort: this.policyResearchSort,
      columnSettings: this.policyResearchColumnSettings,
    });
  }

  private saveContractResearchFilters(): void {
    savePersistedResearchFilters(CONTRACTS_FILTERS_STORAGE_KEY, this.mainStore?.filtersScope ?? null, {
      filters: Object.fromEntries(CONTRACTS_FILTER_FIELDS.map((field) => [field, this.filters[field]])),
      columnFilters: this.columnFilters,
      sort: this.contractsSort,
    });
  }

  // Fetches the first batch of policies for the browsable policy research table (payer optional,
  // narrowed by state/line of business/search/column filters). Pass loadMore=true to append the
  // next page instead (called when scrolling to the bottom of the table). Mirrors fetchContracts.
  fetchPolicyResearch = async (loadMore: boolean = false) => {
    let lastPolicy: IPolicyPreviewItem | undefined;
    if (loadMore) {
      if (this.policyResearchIsLoadingMore || !this.policyResearchHasMoreData || this.policyResearchData.policies.length === 0) {
        return;
      }
      lastPolicy = this.policyResearchData.policies[this.policyResearchData.policies.length - 1];
      if (!lastPolicy?.document_id) return;
    } else {
      this.setPolicyResearchHasMoreData(true);
    }

    // Supersede any in-flight policy research request -- see the matching comment on
    // fetchContracts for why this is needed (search/sort/filter/refresh/loadMore races).
    this.policyResearchAbortController?.abort();
    this.setPolicyResearchIsLoadingMore(false);
    this.setIsLoadingPolicyResearch(false);
    const controller = new AbortController();
    this.policyResearchAbortController = controller;
    const requestId = ++this.policyResearchRequestId;

    if (loadMore) {
      this.setPolicyResearchIsLoadingMore(true);
    } else {
      this.setIsLoadingPolicyResearch(true);
    }

    const sortColumn = (this.policyResearchSort.by || 'title') as keyof IPolicyPreviewItem;
    // Stringify the cursor value for the query param, and send a missing (null) value as
    // "no value" (undefined) rather than the literal string "null".
    const lastSortValue = lastPolicy?.[sortColumn];
    const startAfterSortValue = lastSortValue == null ? undefined : String(lastSortValue);

    try {
      const response = await apiService.getTenantPolicies(
        this.filters.payer,
        isNationalValue(this.filters.state) ? undefined : this.filters.state,
        this.filters.lineOfBusiness,
        loadMore ? lastPolicy?.document_id : undefined,
        loadMore ? startAfterSortValue : undefined,
        this.filters.policySearchQuery,
        this.policyResearchSort.by || undefined,
        this.policyResearchSort.direction,
        this.policyResearchColumnFilters,
        controller.signal,
      );

      // A newer fetchPolicyResearch call superseded this one -- its response is stale
      // and must not overwrite state the newer request already owns.
      if (requestId !== this.policyResearchRequestId) return;

      if (response.success && response.data) {
        if (loadMore) {
          if (response.data.policies.length === 0) {
            this.setPolicyResearchHasMoreData(false);
          } else {
            this.appendPolicyResearchData(response.data.policies);
            if (response.data.policies.length < CONTRACTS_PAGE_SIZE) {
              this.setPolicyResearchHasMoreData(false);
            }
          }
        } else {
          this.setPolicyResearchData(response.data);
          if (response.data.policies.length < CONTRACTS_PAGE_SIZE) {
            this.setPolicyResearchHasMoreData(false);
          }
        }
      } else {
        console.error('Failed to fetch policies:', response.error);
        if (!loadMore && this.policyResearchData.policies.length === 0) {
          this.setPolicyResearchData({ total: 0, policies: [] });
        }
        this.setPolicyResearchHasMoreData(false);
      }
    } catch {
      if (requestId !== this.policyResearchRequestId) return;
      if (!loadMore && this.policyResearchData.policies.length === 0) {
        this.setPolicyResearchData({ total: 0, policies: [] });
        this.mainStore?.appStore.setPolicyToPreview(null);
      }
      this.setPolicyResearchHasMoreData(false);
    } finally {
      if (requestId === this.policyResearchRequestId) {
        if (loadMore) {
          this.setPolicyResearchIsLoadingMore(false);
        } else {
          this.setIsLoadingPolicyResearch(false);
          this.setIsSearching(false);
        }
      }
    }
  };

}

